import { styled } from "@mui/material/styles";

import { Toggle } from "@/common/components/Toggle/Toggle";

import { FieldError } from "../FieldParts";
import { Container, Label } from "../shared.styles";
import { useSubmitFirstField } from "../useSubmitFirstField";

type ToggleFieldBaseProps = {
  label: React.ReactNode;
  name: string;
  value: boolean;
  disabled?: boolean;
  error?: React.ReactNode;
  fullWidth?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const ToggleFieldBase = ({
  disabled,
  error,
  fullWidth,
  label,
  name,
  onBlur,
  onChange,
  value,
}: ToggleFieldBaseProps): React.ReactElement => (
  <Container $fullWidth={fullWidth}>
    <Row>
      <Toggle
        checked={value}
        data-testid={name}
        disabled={disabled}
        id={name}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
      />
      <ClickableLabel htmlFor={name}>{label}</ClickableLabel>
    </Row>
    <FieldError error={error} name={name} />
  </Container>
);

type ToggleFieldProps = {
  label: React.ReactNode;
  name: string;
  disabled?: boolean;
  fullWidth?: boolean;
  onValueChange?: (value: boolean) => void;
};

const ToggleField = ({
  name,
  onValueChange,
  ...props
}: ToggleFieldProps): React.ReactElement => {
  const { error, handleBlur, handleChange, value } =
    useSubmitFirstField<boolean>(name);

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    handleChange(e);
    onValueChange?.(e.target.checked);
  };

  return (
    <ToggleFieldBase
      {...props}
      error={error}
      name={name}
      onBlur={handleBlur}
      onChange={handleFieldChange}
      value={value}
    />
  );
};

const Row = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const ClickableLabel = styled(Label)({
  cursor: "pointer",
  lineHeight: 1.4,
});

export {
  ToggleField,
  ToggleFieldBase,
  type ToggleFieldBaseProps,
  type ToggleFieldProps,
};
